import {
  useEffectWithController,
  useLoadableState
} from '@carvana/merch-react-hooks';
import { useContext } from 'react';
import TransitChargeState from '~/constants/common/TransitChargeState';
import CommonStaticContext, {
  useEventCorrelationId
} from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import type { MatchingVehicleWithDeltas } from '~/hooks/new-cars/selectors/useMatchingVehiclesWithDeltas';
import fetchVehiclesPricing from '~/services/common/fetchVehiclesPricing';
import type { TransitChargeInfo } from '~/types/TransitChargeInfo';
import { generateErrorLogger } from '~/utilities/loggers';

function toTransitCharge(shippingFee: number | undefined): TransitChargeInfo {
  return {
    charge: shippingFee ?? 0,
    isLoaded: true,
    isLoading: false,
    state:
      shippingFee === undefined
        ? TransitChargeState.Unknown
        : shippingFee > 0
        ? TransitChargeState.HasCharge
        : TransitChargeState.NoCharge
  };
}

export default function useMatchingVehiclesPricing(
  vehicles: MatchingVehicleWithDeltas[]
) {
  const { clientId } = useContext(CommonStaticContext);
  const correlationId = useEventCorrelationId();
  const { zip } = useUserLocation();

  const pricing = useLoadableState<Record<number, TransitChargeInfo>>({});
  const { setData, setError, setLoading } = pricing;

  useEffectWithController(
    controller => {
      if (!zip || vehicles.length === 0) return;

      setLoading();

      const vehicleIds = vehicles.map(vehicle => vehicle.vehicleId);

      fetchVehiclesPricing(
        { clientId, correlationId, vehicleIds, zip },
        controller
      )
        .then(({ data }) => {
          setData(
            Object.entries(data.vehiclePaymentTermsMapping).reduce<
              Record<number, TransitChargeInfo>
            >((acc, [vehicleId, term]) => {
              acc[Number(vehicleId)] = toTransitCharge(
                term.downPaymentDetails?.nonRefundableShippingFees
              );

              return acc;
            }, {})
          );
        })
        .catch(
          generateErrorLogger(
            'Get matching vehicles pricing',
            {},
            { callback: setError, controller }
          )
        );
    },
    [clientId, correlationId, setData, setError, setLoading, vehicles, zip]
  );

  return pricing;
}
